import React from "react";
import { cn } from "../../lib/utils";

/**
 * ContentContainer - Reusable container for main content areas
 * Provides consistent spacing and background colors
 */
export function ContentContainer({
  children,
  className = "",
  variant = "default", // default, elevated, outlined
  padding = "p-6",
  background = "bg-neutral-1",
}) {
  const variantStyles = {
    default: "bg-neutral-1",
    elevated: "bg-neutral-1 shadow-sm border border-neutral-4 rounded-lg",
    outlined: "bg-neutral-1 border border-neutral-5 rounded-lg",
  };

  return (
    <div
      className={cn(
        variantStyles[variant] || variantStyles.default,
        background !== "bg-neutral-1" && background, // Override background if provided
        padding,
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * PageHeader - Consistent page header with title and actions
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  className = "",
  titleClassName = "",
  subtitleClassName = "",
  actionsClassName = "",
}) {
  return (
    <div className={cn("flex items-center justify-between mb-6", className)}>
      <div className="flex-1">
        {title && (
          <h1
            className={cn(
              "text-2xl font-semibold text-neutral-12 mb-1",
              titleClassName
            )}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p className={cn("text-neutral-8", subtitleClassName)}>
            {subtitle}
          </p>
        )}
      </div>
      
      {actions && (
        <div className={cn("flex items-center space-x-3", actionsClassName)}>
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * Section - Reusable section container with optional header
 */
export function Section({
  children,
  title,
  subtitle,
  headerActions,
  className = "",
  headerClassName = "",
  contentClassName = "",
  variant = "default",
}) {
  return (
    <section className={cn("mb-8", className)}>
      {(title || subtitle || headerActions) && (
        <div
          className={cn(
            "flex items-center justify-between mb-4",
            headerClassName
          )}
        >
          <div>
            {title && (
              <h2 className="text-lg font-medium text-neutral-12 mb-1">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-neutral-8">{subtitle}</p>
            )}
          </div>
          
          {headerActions && (
            <div className="flex items-center space-x-2">
              {headerActions}
            </div>
          )}
        </div>
      )}
      
      <ContentContainer
        variant={variant}
        className={contentClassName}
      >
        {children}
      </ContentContainer>
    </section>
  );
}

/**
 * Grid layouts for common patterns
 */
export function GridLayout({
  children,
  columns = "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  gap = "gap-6",
  className = "",
}) {
  return (
    <div className={cn("grid", columns, gap, className)}>
      {children}
    </div>
  );
}

/**
 * Flex layouts for common patterns
 */
export function FlexLayout({
  children,
  direction = "flex-row",
  justify = "justify-start",
  align = "items-start",
  gap = "gap-4",
  wrap = "flex-wrap",
  className = "",
}) {
  return (
    <div className={cn("flex", direction, justify, align, gap, wrap, className)}>
      {children}
    </div>
  );
}

/**
 * Stack layout for vertical spacing
 */
export function Stack({
  children,
  spacing = "space-y-4",
  className = "",
}) {
  return (
    <div className={cn("flex flex-col", spacing, className)}>
      {children}
    </div>
  );
}
